import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { mediaApi } from './api';
import { keys } from '@/lib/query/keys';
import { useListState, useLocalListState, type ListState } from '@/lib/hooks/useListState';
import { useSearchInput } from '@/lib/hooks/useSearchInput';
import { DataView } from '@/lib/ui/states';
import { Pagination } from '@/lib/ui/Pagination';
import { FilterBar } from '@/lib/ui/FilterBar';
import { ListEmpty } from '@/lib/ui/ListEmpty';
import { Select } from '@/lib/ui/Select';
import { TextInput } from '@/lib/ui/Field';
import { cn } from '@/lib/ui/cn';
import { MonoValue } from '@/lib/ui/Typography';
import { formatBytes } from '@/lib/format';
import type { MediaAsset, MediaKind } from '@/lib/api/types';
import { UploadDropzone } from './UploadDropzone';
import { thumbUrl } from './derivatives';

const KIND_OPTIONS = [
  { value: '', labelKey: 'media.kindAll' },
  { value: 'image', labelKey: 'media.kindImages' },
  { value: 'svg', labelKey: 'media.kindSvg' },
  { value: 'pdf', labelKey: 'media.kindPdf' },
  { value: 'doc', labelKey: 'media.kindDocuments' },
  { value: 'video', labelKey: 'media.kindVideo' },
];

const KIND_ICON: Record<MediaKind, string> = {
  image: '🖼',
  svg: '⬡',
  pdf: '📄',
  doc: '📝',
  video: '▶',
};

interface MediaBrowserProps {
  onSelect?: (asset: MediaAsset) => void;
  canUpload?: boolean;
  selectedId?: string | null;
  /**
   * Lock the browser to one media kind (a field that only accepts a PDF), or to several (a logo
   * may be an image or an SVG; the server takes `kind=image,svg`).
   */
  kind?: MediaKind | readonly MediaKind[];
  /**
   * Who owns the list state. The library page is the reason you are at its address, so its
   * filters live in the URL; the picker is a tool opened over some other screen, so its
   * filters stay local and never rewrite the host's URL (PHASE 12 decision).
   */
  scope: 'page' | 'modal';
}

/** Reusable media grid (library page + picker). Lazy thumbnails, filters, debounced search. */
export function MediaBrowser({ scope, ...props }: MediaBrowserProps) {
  return scope === 'page' ? <UrlMediaBrowser {...props} /> : <LocalMediaBrowser {...props} />;
}

type ViewProps = Omit<MediaBrowserProps, 'scope'>;

/** The `kind` query value of a lock: one kind, or a comma-separated list. */
export function kindParam(kind: MediaBrowserProps['kind']): string {
  if (!kind) return '';
  return typeof kind === 'string' ? kind : kind.join(',');
}

function UrlMediaBrowser(props: ViewProps) {
  const list = useListState({ kind: kindParam(props.kind) });
  return <MediaBrowserView {...props} list={list} />;
}

function LocalMediaBrowser(props: ViewProps) {
  const list = useLocalListState({ kind: kindParam(props.kind) });
  return <MediaBrowserView {...props} list={list} />;
}

function MediaBrowserView({ onSelect, canUpload, selectedId, kind, list }: ViewProps & { list: ListState }) {
  const { t } = useTranslation();
  const [rawSearch, setRawSearch] = useSearchInput(list);

  const query = useQuery({
    queryKey: keys.media.list(list.params),
    queryFn: () => mediaApi.list(list.params),
    placeholderData: (prev) => prev,
  });

  return (
    <div className="flex flex-col gap-md">
      {canUpload && <UploadDropzone />}
      <FilterBar onClearAll={list.reset} active={list.active && !(kind && !list.search)} className="items-end">
        <div className="w-[160px]">
          <Select
            label={t('media.kind')}
            value={(list.filters.kind as string) || ''}
            onValueChange={(v) => list.setFilter('kind', v)}
            disabled={!!kind}
            options={[
              ...KIND_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
              // A multi-kind lock (`image,svg`) is shown as its own (disabled) choice.
              ...(kind && !KIND_OPTIONS.some((o) => o.value === kindParam(kind))
                ? [{ value: kindParam(kind), label: t('media.kindImagesAndSvg') }]
                : []),
            ]}
          />
        </div>
        <div className="min-w-[200px] flex-1">
          <TextInput
            label={t('common.search')}
            placeholder={t('media.searchPlaceholder')}
            value={rawSearch}
            onChange={(e) => setRawSearch(e.target.value)}
          />
        </div>
      </FilterBar>

      <DataView
        query={query}
        isEmpty={(d) => d.data.length === 0}
        copy={{ loading: t('states.media.loading'), errorTitle: t('states.media.errorTitle') }}
        empty={
          <ListEmpty
            filtered={list.active && !(kind && !list.search)}
            onClearFilters={list.reset}
            title={t('states.media.emptyTitle')}
            body={t(canUpload ? 'states.media.libraryEmptyBody' : 'states.media.emptyBody')}
          />
        }
      >
        {(d) => (
          <>
            <ul className="grid grid-cols-2 gap-md sm:grid-cols-3 lg:grid-cols-4">
              {d.data.map((asset) => (
                <li key={asset.id}>
                  <button
                    type="button"
                    onClick={() => onSelect?.(asset)}
                    className={cn(
                      'group flex w-full flex-col overflow-hidden rounded-md border-[0.5px] border-hairline bg-canvas-paper text-start transition-transform duration-almanac ease-almanac focus-visible:shadow-focus',
                      onSelect && 'cursor-pointer hover:-translate-y-[2px] hover:border-accent-blue',
                      selectedId === asset.id && 'border-accent-blue ring-2 ring-accent-blue/30',
                    )}
                  >
                    <div className="flex aspect-[4/3] items-center justify-center bg-canvas-deep-sand">
                      {asset.kind === 'image' && !asset.is_restricted ? (
                        <img
                          src={thumbUrl(asset)}
                          alt={asset.alt?.en ?? asset.file_name}
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="text-[2rem]" aria-hidden>
                          {KIND_ICON[asset.kind]}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col gap-[2px] p-sm">
                      <span className="truncate text-small text-ink-graphite">{asset.file_name}</span>
                      <span className="font-mono text-label-sm text-ink-mute">
                        {asset.width > 0 && (
                          <MonoValue>
                            {asset.width}×{asset.height}
                          </MonoValue>
                        )}{' '}
                        · {formatBytes(asset.size_bytes)}
                        {asset.is_restricted && ' · 🔒'}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
            {d.meta?.pagination && (
              <Pagination
                meta={d.meta.pagination}
                onPageChange={list.setPage}
                onPerPageChange={list.setPerPage}
              />
            )}
          </>
        )}
      </DataView>
    </div>
  );
}
